import React, { useState, useEffect, useCallback, useRef } from 'react';
import AuthGuard from './AuthGuard';
import {
  Admin,
  Resource,
  Layout,
  Sidebar,
  Menu,
  MenuItem,
  useDataProvider,
  useGetList,
  useNotify
} from 'react-admin';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Bot,
  MessageSquare,
  Share2,
  BookOpen,
  Brain,
  Globe,
  TrendingUp,
  ThumbsUp,
  Code,
  BarChart3,
  Settings,
  Plus,
  Menu as MenuIcon,
  X,
  Clock,
  User,
  ChevronDown,
  Search,
  MessageCircle,
  ChevronRight,
  Star,
  History,
  MoreHorizontal
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ContextUpload from './ContextUpload';
import ChatInterface from './ChatInterface';
import EmbedAgent from './EmbedAgent';
import ConversationHistory from './ConversationHistory';
import AgentSettings from './AgentSettings';
import WebsiteScanner from './WebsiteScanner';
import TuneYourAgent from './TuneYourAgent';
import ImproveAgent from './ImproveAgent';
import UserDropdown from './UserDropdown';
import PublicChatInterface from './PublicChatInterface';
import FirebaseAuth from './FirebaseAuth';
import './ReactAdminPrototype.css';

// Custom Layout Component
const CustomLayout = (props) => {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  
  // Determine mode based on current route
  const mode = location.pathname === '/' || location.pathname.startsWith('/talkto') ? 'discover' : 'build';
  
  const [recentConversations, setRecentConversations] = useState([]);
  const [showAllConversations, setShowAllConversations] = useState(false);
  const [expandedSubsections, setExpandedSubsections] = useState(['recent-conversations', 'my-chats', 'pendle']);
  const [recentChats, setRecentChats] = useState([]);
  const [showAllChats, setShowAllChats] = useState(false);
  const [loadingChats, setLoadingChats] = useState(true);
  const [pendleAgents, setPendleAgents] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [isInChat, setIsInChat] = useState(false);
  const [notifications, setNotifications] = useState(0);
  const [viewConversationsList, setViewConversationsList] = useState([]);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [authMode, setAuthMode] = useState('signin');
  const [agentReadiness, setAgentReadiness] = useState(null);
  
  // Close modal when user becomes authenticated
  useEffect(() => {
    if (user && showSignupModal) {
      console.log('User authenticated, closing modal');
      setShowSignupModal(false);
    }
  }, [user, showSignupModal]);

  // React Admin expects children to be rendered
  const { children } = props;
  const { getToken } = useAuth();

  // Listen for unread count updates from ConversationHistory component
  useEffect(() => {
    const handleUnreadCountUpdate = (event) => {
      const { count, conversations } = event.detail;
      setNotifications(count);
      setViewConversationsList(conversations || []);
    };

    window.addEventListener('viewConversationsUnreadCount', handleUnreadCountUpdate);

    return () => {
      window.removeEventListener('viewConversationsUnreadCount', handleUnreadCountUpdate);
    };
  }, []);

  // Load recent chats for Discover mode (conversations with other agents)
  const loadRecentChats = async () => {
    setLoadingChats(true);
    try {
      // Check if we should use mock data (for screenshots/demos)
      const useMockData = window.location.search.includes('mock=true') || 
                         (window.location.hostname === 'localhost' && 
                          localStorage.getItem('useMockAgents') === 'true');
      
      console.log('ReactAdminPrototype mock data detection:', {
        search: window.location.search,
        hostname: window.location.hostname,
        localStorage: localStorage.getItem('useMockAgents'),
        useMockData: useMockData,
        isAuthenticated: isAuthenticated
      });
      
      // If using mock data, load it regardless of authentication status
      if (useMockData) {
        const mockParam = '?mock=true';
        console.log('Loading mock conversations from ReactAdminPrototype:', `/api/agents/public/my-conversations${mockParam}`);
        const response = await fetch(`/api/agents/public/my-conversations${mockParam}`);
        console.log('Mock conversations response status:', response.status);
        if (response.ok) {
          const data = await response.json();
          console.log('Loaded mock conversations:', data.conversations?.length || 0);
          console.log('Mock conversation titles:', data.conversations?.map(c => c.title));
          setRecentChats(data.conversations || []);
          // Not used - notification badge is for View Conversations only
          setLoadingChats(false);
          return;
        } else {
          console.error('Failed to load mock conversations, status:', response.status);
        }
      }
      
      // Only load real conversations if user is authenticated
      if (!isAuthenticated) {
        setRecentChats([]);
        setLoadingChats(false);
        return;
      }
      
      const token = await getToken();
      if (!token) {
        console.error('No auth token available');
        setRecentChats([]);
        setLoadingChats(false);
        return;
      }
      
      const response = await fetch('/api/agents/public/my-conversations', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecentChats(data.conversations || []);
        // Not used - notification badge is for View Conversations only
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error('Failed to load user chats:', response.status, errorText);
        setRecentChats([]);
      }
    } catch (error) {
      console.error('Error loading user chats:', error);
      setRecentChats([]);
    } finally {
      setLoadingChats(false);
    }
  };

  // Also listen for reviewedConversationsUpdated to update count when conversations are marked as reviewed
  useEffect(() => {
    const handleReviewedUpdated = () => {
      // ConversationHistory will dispatch the updated count, so we just need to listen
      // The count will be updated via viewConversationsUnreadCount event
    };

    window.addEventListener('reviewedConversationsUpdated', handleReviewedUpdated);

    return () => {
      window.removeEventListener('reviewedConversationsUpdated', handleReviewedUpdated);
    };
  }, []);

  // Load recent conversations
  const loadRecentConversations = useCallback(async () => {
    if (!user?.agent?.id) return;
    
    try {
      const token = await getToken();
      if (!token) {
        console.error('No auth token available');
        return;
      }
      
      const response = await fetch(`/api/agents/${user.agent.id}/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter to only show owner conversations (agent owner chatting with their own agent)
        // Owner conversations are: is_embedded = false AND user_id IS NULL
        const ownerConversations = (data.conversations || []).filter(conv => 
          !conv.is_embedded && !conv.user_id
        );
        setRecentConversations(ownerConversations);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  }, [user?.agent?.id, getToken]);

  // Handle URL parameters for chat navigation
  useEffect(() => {
    const chatId = searchParams.get('chat');
    const agentId = searchParams.get('agent');
    
    console.log('🔍 [REACT_ADMIN_URL_EFFECT] useEffect triggered');
    console.log('🔍 [REACT_ADMIN_URL_EFFECT] Current URL:', window.location.href);
    console.log('🔍 [REACT_ADMIN_URL_EFFECT] URL params:', { chatId, agentId, recentChats: recentChats.length });
    console.log('🔍 [REACT_ADMIN_URL_EFFECT] searchParams object:', Object.fromEntries(searchParams.entries()));
    
    if (chatId) {
      // If we have a chatId in URL, try to find the chat
      console.log('🔍 [REACT_ADMIN_URL_EFFECT] Branch: chatId found in URL');
      console.log('🔍 [REACT_ADMIN_URL_EFFECT] Looking for chat with ID:', chatId, 'Type:', typeof chatId);
      console.log('🔍 [REACT_ADMIN_URL_EFFECT] Available chats:', recentChats.map(c => ({ id: c.id, type: typeof c.id })));
      const chat = recentChats.find(c => c.id.toString() === chatId);
      console.log('🔍 [REACT_ADMIN_URL_EFFECT] Found chat:', chat ? 'YES' : 'NO', chat);
      
      if (chat) {
        // Chat found, load it
        console.log('✅ [REACT_ADMIN_URL_EFFECT] Chat found, loading it');
        setSelectedConversationId(chatId);
        const agentData = {
          id: chat.agent_id,
          name: chat.agent_name,
          agent_avatar: chat.agent_avatar,
          creator_name: chat.creator_name,
          creator_picture: chat.creator_picture
        };
        setSelectedAgent(agentData);
        setIsInChat(true);
        console.log('✅ [REACT_ADMIN_URL_EFFECT] Chat loaded successfully');
      } else if (agentId) {
        // We have both chatId and agentId - this is a newly created conversation
        // Don't clear the URL, just load the agent (chat will be loaded by PublicChatInterface)
        console.log('✅ [REACT_ADMIN_URL_EFFECT] New conversation detected (has both chatId and agentId), loading agent');
        fetch(`/api/agents/public`)
          .then(response => response.json())
          .then(data => {
            const agent = data.agents?.find(a => a.id === parseInt(agentId));
            if (agent) {
              setSelectedAgent(agent);
              setSelectedConversationId(chatId);
              setIsInChat(true);
              console.log('✅ [REACT_ADMIN_URL_EFFECT] Agent and conversation loaded');
            }
          })
          .catch(error => {
            console.error('❌ [REACT_ADMIN_URL_EFFECT] Error loading agent:', error);
          });
      } else if (recentChats.length > 0 && !agentId) {
        // Chat not found, no agentId, and chats are loaded - might be invalid
        // Only clear if agentId is NOT present (if agentId is present, it's a new conversation)
        console.log('⚠️ [REACT_ADMIN_URL_EFFECT] Chat not found in recentChats, but chats are loaded. This may be a new conversation.');
        // Give a longer moment for conversationCreated event to refresh the list
        setTimeout(() => {
          const currentChatId = searchParams.get('chat');
          const currentAgentId = searchParams.get('agent');
          // Only clear if agentId is still not present (don't clear new conversations)
          if (!currentAgentId) {
            const stillNotFound = currentChatId && !recentChats.find(c => c.id.toString() === currentChatId);
            if (stillNotFound) {
              console.log('⚠️ [REACT_ADMIN_URL_EFFECT] Chat still not found after wait, clearing URL parameters');
              setSearchParams({});
            }
          } else {
            console.log('✅ [REACT_ADMIN_URL_EFFECT] AgentId present, not clearing URL (new conversation)');
          }
        }, 5000); // Increased timeout
      }
      // If recentChats is still loading (length === 0), wait for it to load
    } else if (agentId) {
      // If we have an agentId but no chatId, just load the agent (no conversation creation)
      console.log('🔍 [REACT_ADMIN_URL] URL has agentId but no chatId:', agentId);
      
      // Fetch agent data
      fetch(`/api/agents/public`)
        .then(response => response.json())
        .then(data => {
          const agent = data.agents?.find(a => a.id === parseInt(agentId));
          if (agent) {
            console.log('✅ [REACT_ADMIN_URL] Found agent:', agent.id, agent.name);
            setSelectedAgent(agent);
            setSelectedConversationId(null); // No conversation yet
            setIsInChat(true);
          } else {
            console.error('❌ [REACT_ADMIN_URL] Agent not found, clearing URL');
            setSearchParams({});
          }
        })
        .catch(error => {
          console.error('❌ [REACT_ADMIN_URL] Error fetching agent:', error);
          setSearchParams({});
        });
    } else {
      // No chat or agent in URL, exit chat mode
      console.log('🔍 [REACT_ADMIN_URL_EFFECT] Branch: No chat or agent in URL, exiting chat mode');
      setIsInChat(false);
      setSelectedAgent(null);
      setSelectedConversationId(null);
      console.log('✅ [REACT_ADMIN_URL_EFFECT] Exited chat mode');
    }
  }, [searchParams, recentChats, setSearchParams, getToken]);

  // Listen for agent chat events
  useEffect(() => {
    const handleStartAgentChat = (event) => {
      const agent = event.detail.agent;
      console.log('🚀 [REACT_ADMIN_EVENT] startAgentChat event received');
      console.log('🚀 [REACT_ADMIN_EVENT] Agent details:', { id: agent.id, name: agent.name });
      
      // Simply set agent and URL - no conversation creation yet
      setSelectedAgent(agent);
      setSelectedConversationId(null); // No conversation yet
      setIsInChat(true);
      setSearchParams({ agent: agent.id.toString() });
      console.log('✅ [REACT_ADMIN_EVENT] Set URL to ?agent=' + agent.id + ' (no conversation created yet)');
    };

    const handleConversationCreated = () => {
      // Refresh conversations when a new one is created
      // Refresh both Discover mode chats and Build mode conversations
      loadRecentChats();
      if (user?.agent?.id) {
        loadRecentConversations();
      }
    };

    const handleConversationDeleted = () => {
      // Refresh conversations when one is deleted
      // Refresh both Discover mode chats and Build mode conversations
      loadRecentChats();
      if (user?.agent?.id) {
        loadRecentConversations();
      }
    };

    const handleUpdateChatUrl = (event) => {
      const conversationId = event.detail.conversationId;
      console.log('🔗 [REACT_ADMIN] Updating URL with conversation ID:', conversationId);
      // Manually construct URL string to preserve param order (agent first, then chat)
      const currentParams = new URLSearchParams(window.location.search);
      const newSearch = new URLSearchParams();
      
      // Put agent param first if it exists
      if (currentParams.get('agent')) {
        newSearch.set('agent', currentParams.get('agent'));
      }
      
      // Then add chat param
      newSearch.set('chat', conversationId.toString());
      
      console.log('🔗 [REACT_ADMIN] New URL string:', newSearch.toString());
      setSearchParams(newSearch, { replace: true });
      console.log('✅ [REACT_ADMIN] URL updated to include chat param');
    };

    window.addEventListener('startAgentChat', handleStartAgentChat);
    window.addEventListener('conversationCreated', handleConversationCreated);
    window.addEventListener('conversationDeleted', handleConversationDeleted);
    window.addEventListener('updateChatUrl', handleUpdateChatUrl);
    
    return () => {
      window.removeEventListener('startAgentChat', handleStartAgentChat);
      window.removeEventListener('conversationCreated', handleConversationCreated);
      window.removeEventListener('conversationDeleted', handleConversationDeleted);
      window.removeEventListener('updateChatUrl', handleUpdateChatUrl);
    };
  }, [loadRecentChats, loadRecentConversations, user?.agent?.id, setSearchParams]);

  // Allow unauthenticated users to view the interface
  // Individual interactive elements will be wrapped with AuthGuard

  useEffect(() => {
    if (user?.agent?.id) {
      loadRecentConversations();
    }
  }, [user?.agent?.id]);

  useEffect(() => {
    if (user) {
      loadRecentChats();
    }
  }, [user]);

  // Load chats when switching to Discover mode
  useEffect(() => {
    if (mode === 'discover' && user) {
      loadRecentChats();
    }
  }, [mode, user]);

  // Load pendle-tagged agents
  const loadPendleAgents = useCallback(async () => {
    try {
      const response = await fetch('/api/agents/by-tag/pendle');
      if (response.ok) {
        const data = await response.json();
        // Filter out "My Agent" - it should only show for authenticated users with their own agent
        const filteredAgents = (data.agents || []).filter(agent => 
          agent.name !== 'My Agent' || (isAuthenticated && user?.agent?.id === agent.id)
        );
        setPendleAgents(filteredAgents);
      } else {
        console.error('Failed to load pendle agents');
        setPendleAgents([]);
      }
    } catch (error) {
      console.error('Error loading pendle agents:', error);
      setPendleAgents([]);
    }
  }, [isAuthenticated, user?.agent?.id]);

  // Load pendle agents when in discover mode
  useEffect(() => {
    if (mode === 'discover') {
      loadPendleAgents();
    } else {
      // Clear pendle agents when not in discover mode
      setPendleAgents([]);
    }
  }, [mode, loadPendleAgents]);

  // Load agent readiness status when in build mode
  const loadAgentReadiness = useCallback(async () => {
    if (mode !== 'build' || !isAuthenticated || !user) return;
    
    try {
      const token = await getToken();
      if (!token) return;
      
      const response = await fetch('/api/agents/readiness', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAgentReadiness(data);
      }
    } catch (error) {
      console.error('Error loading agent readiness:', error);
    }
  }, [mode, isAuthenticated, user, getToken]);

  useEffect(() => {
    loadAgentReadiness();
    // Refresh readiness every 30 seconds
    const interval = setInterval(loadAgentReadiness, 30000);
    return () => clearInterval(interval);
  }, [loadAgentReadiness]);
  
  // Clear pendle agents when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setPendleAgents([]);
    }
  }, [isAuthenticated]);

  // Show loading while checking authentication
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '20px',
        background: 'linear-gradient(135deg, #0066FF 0%, #00CCFF 100%)'
      }}>
        <img 
          src={process.env.PUBLIC_URL + "/YourQuestionsAnsweredLogo.png"} 
          alt="Question Answer Pro Logo" 
          width={80}
          height={80}
          style={{
            filter: 'drop-shadow(0 4px 12px rgba(0, 102, 255, 0.3))'
          }}
        />
        <div style={{ color: 'white', fontSize: '1.125rem', fontWeight: 500 }}>Loading...</div>
      </div>
    );
  }

  // Allow unauthenticated users to view the interface
  // Individual interactive elements will be wrapped with AuthGuard

  // Toggle subsection expansion
  const toggleSubsection = (subsectionId) => {
    setExpandedSubsections(prev => 
      prev.includes(subsectionId) 
        ? prev.filter(id => id !== subsectionId)
        : [...prev, subsectionId]
    );
  };

  // Handle conversation click
  const handleConversationClick = (conversationId) => {
    navigate(`/build/c/${conversationId}`);
  };

  // Handle chat click (for Discover mode)
  const handleChatClick = (chat) => {
    console.log('Chat clicked:', chat);
    // Set chat state directly for immediate response
    setSelectedConversationId(chat.id);
    const agentData = {
      id: chat.agent_id,
      name: chat.agent_name,
      agent_avatar: chat.agent_avatar,
      creator_name: chat.creator_name,
      creator_picture: chat.creator_picture
    };
    setSelectedAgent(agentData);
    setIsInChat(true);
    
    // Also update URL parameters
    setSearchParams({ chat: chat.id.toString() });
  };

  const handleBackFromChat = () => {
    // Clear URL parameters to go back to discover page
    setSearchParams({});
    console.log('Back from chat - navigating to discover');
  };

  const handleUpdateUrl = (params) => {
    console.log('🔗 [REACT_ADMIN_HANDLE_UPDATE_URL] Called with params:', params);
    console.log('🔗 [REACT_ADMIN_HANDLE_UPDATE_URL] Current URL before update:', window.location.href);
    // Manually construct URL string to preserve param order (agent first, then chat)
    const currentParams = new URLSearchParams(window.location.search);
    const newSearch = new URLSearchParams();
    
    // Put agent param first if it exists (either from current URL or new params)
    if (params.agent || currentParams.get('agent')) {
      newSearch.set('agent', params.agent || currentParams.get('agent'));
    }
    
    // Then add chat param if it exists
    if (params.chat) {
      newSearch.set('chat', params.chat);
    }
    
    // Preserve any other params
    for (const [key, value] of currentParams.entries()) {
      if (key !== 'agent' && key !== 'chat' && !params[key]) {
        newSearch.set(key, value);
      }
    }
    
    const newUrl = newSearch.toString();
    console.log('🔗 [REACT_ADMIN_HANDLE_UPDATE_URL] New URL string:', newUrl);
    setSearchParams(newSearch, { replace: true });
    console.log('🔗 [REACT_ADMIN_HANDLE_UPDATE_URL] setSearchParams called');
    setTimeout(() => {
      console.log('🔗 [REACT_ADMIN_HANDLE_UPDATE_URL] URL after update (delayed check):', window.location.href);
    }, 100);
  };

  return (
    <div className="react-admin-prototype" data-theme="dark">
      {/* App Header - Match /build exactly */}
      <header className="app-header">
        <div className="header-content">
          <h1 className="app-title" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img 
              src={process.env.PUBLIC_URL + "/YourQuestionsAnsweredLogo.png"} 
              alt="Brain Logo" 
              className="title-icon"
            />
            <span className="app-title-text">Question Answer Pro</span>
          </h1>
          <p className="app-subtitle">Answers at the speed of AI, backed by human experts</p>
          <div className={`mode-toggle ${mode === 'discover' ? 'discover-mode' : ''}`}>
            <button 
              className={`toggle-button ${mode === 'discover' ? 'active' : ''}`}
              onClick={() => navigate('/')}
            >
              Discover
            </button>
            <AuthGuard>
              <button 
                className={`toggle-button ${mode === 'build' ? 'active' : ''}`}
                onClick={() => navigate('/build')}
              >
                Admin
              </button>
            </AuthGuard>
          </div>

          <AuthGuard 
            fallback={
              <div className="user-section">
                <div className="auth-buttons-container">
                  <button 
                    className="signup-button"
                    onClick={() => {
                      setAuthMode('signup');
                      setShowSignupModal(true);
                    }}
                  >
                    Sign Up
                  </button>
                  <button 
                    className="signin-button"
                    onClick={() => {
                      setAuthMode('signin');
                      setShowSignupModal(true);
                    }}
                  >
                    Sign In
                  </button>
                </div>
              </div>
            }
          >
            <div className="user-section">
              <div className="user-info" onClick={() => setShowUserDropdown(!showUserDropdown)}>
                <span className="user-name">{user?.name || 'User'}</span>
                <span className="user-email">{user?.email || 'user@example.com'}</span>
              </div>
              <div className="user-avatar" onClick={() => setShowUserDropdown(!showUserDropdown)}>
                {user?.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const defaultIcon = e.target.parentElement.querySelector('.default-avatar-icon');
                      if (defaultIcon) {
                        defaultIcon.style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <div 
                  className="default-avatar-icon" 
                  style={{ 
                    display: user?.picture ? 'none' : 'flex',
                    width: '100%',
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <User size={24} />
                </div>
              </div>
              {showUserDropdown && (
                <UserDropdown 
                  user={user} 
                  onClose={() => setShowUserDropdown(false)} 
                />
              )}
            </div>
          </AuthGuard>
        </div>
        
        {/* Mobile Menu Button - Only visible on mobile */}
        <button 
          className="mobile-menu-button"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X size={24} /> : <MenuIcon size={24} />}
        </button>
        
        {/* Mobile Toggle - Only visible on mobile */}
        <div className="mobile-mode-toggle">
          <button 
            className={`mobile-toggle-button ${mode === 'discover' ? 'active' : ''}`}
            onClick={() => navigate('/talkto')}
            title="Discover Mode"
          >
            <Search size={16} />
          </button>
          <AuthGuard>
            <button 
              className={`mobile-toggle-button ${mode === 'build' ? 'active' : ''}`}
              onClick={() => navigate('/build')}
              title="Admin Mode"
            >
              <Bot size={16} />
            </button>
          </AuthGuard>
        </div>
      </header>

      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <nav className="sidebar-nav">
          {/* Build Navigation */}
          {mode === 'build' && (
            <>
              {!isAuthenticated ? (
                <div className="nav-section">
                  <div className="section-header">
                    <Bot size={20} />
                    Build Your Agent
                  </div>
                  <div className="section-items">
                    <div 
                      className="nav-item-disabled"
                      onClick={() => {
                        setAuthMode('signup');
                        setShowSignupModal(true);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <BookOpen size={20} />
                      <div className="item-content">
                        <div className="item-title">Sign up to build your agent</div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="nav-section">
                    <button className="section-header">
                      <Bot size={20} />
                      Build Your Agent
                    </button>
                    <div className="section-items">
                      <button className="nav-item" onClick={() => navigate('/build/upload')}>
                        <BookOpen size={20} />
                        <div className="item-content">
                          <div className="item-title">Upload Training Agent Files</div>
                        </div>
                      </button>
                      <button className="nav-item" onClick={() => navigate('/build/text')}>
                        <Brain size={20} />
                        <div className="item-content">
                          <div className="item-title">Instruct Your Agent</div>
                        </div>
                      </button>
                      {user?.beta_tester && (
                        <button className="nav-item" onClick={() => navigate('/build/websites')}>
                          <Globe size={20} />
                          <div className="item-content">
                            <div className="item-title">Scan Websites</div>
                          </div>
                        </button>
                      )}
                      {process.env.NODE_ENV !== 'production' && (
                        <button className="nav-item" onClick={() => navigate('/build/tune')}>
                          <TrendingUp size={20} />
                          <div className="item-content">
                            <div className="item-title">Answer Agent Questions</div>
                          </div>
                        </button>
                      )}
                      <button className="nav-item" onClick={() => navigate('/build/improve')}>
                        <ThumbsUp size={20} />
                        <div className="item-content">
                          <div className="item-title">Manage Agent Feedback</div>
                        </div>
                      </button>
                    </div>
                  </div>

                  <div className="nav-section">
                    <button className="section-header">
                      <MessageSquare size={20} />
                      Test Your Agent
                    </button>
                    <div className="section-items">
                      <button className="nav-item" onClick={() => {
                        navigate('/build');
                        // Dispatch event to start new chat (works even if already on /build)
                        window.dispatchEvent(new CustomEvent('startNewChat'));
                      }}>
                        <Plus size={20} />
                        <div className="item-content">
                          <div className="item-title">New Chat</div>
                        </div>
                      </button>
                      
                      {/* Recent Conversations Subsection */}
                      {recentConversations.length > 0 && (
                        <div className="conversations-subsection">
                          <button
                            className="subsection-header"
                            onClick={() => toggleSubsection('recent-conversations')}
                          >
                            <History size={14} />
                            <span>Recent Test Conversations</span>
                            {expandedSubsections.includes('recent-conversations') ? (
                              <ChevronDown size={14} />
                            ) : (
                              <ChevronRight size={14} />
                            )}
                          </button>
                          {expandedSubsections.includes('recent-conversations') && (
                            <div className="conversations-list">
                              {(showAllConversations ? recentConversations : recentConversations.slice(0, 5)).map((conv) => (
                                <button
                                  key={conv.id}
                                  className="nav-item"
                                  onClick={() => handleConversationClick(conv.id)}
                                >
                                  <MessageSquare size={16} />
                                  <div className="item-content">
                                    <span className="item-title">
                                      {conv.title && conv.title.trim() ? 
                                        (conv.title.length > 50 ? 
                                          conv.title.substring(0, 50) + '...' : 
                                          conv.title
                                        ) : 
                                        `New Conversation ${conv.id}`
                                      }
                                    </span>
                                  </div>
                                </button>
                              ))}
                              {recentConversations.length > 5 && (
                                <button
                                  className="nav-item"
                                  onClick={() => setShowAllConversations(!showAllConversations)}
                                >
                                  <MoreHorizontal size={16} />
                                  <div className="item-content">
                                    <span className="item-title">
                                      {showAllConversations ? 'Show Less' : `Show All (${recentConversations.length})`}
                                    </span>
                                  </div>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="nav-section">
                    <button 
                      className={`section-header ${agentReadiness?.hasContext && !agentReadiness?.isLaunched ? 'pulsing-action-needed' : ''}`}
                      onClick={() => {
                        navigate('/build/agentsettings');
                      }}
                      style={{
                        cursor: 'pointer'
                      }}
                    >
                      <Share2 size={20} />
                      Launch Your Agent
                      {agentReadiness?.hasContext && !agentReadiness?.isLaunched && (
                        <span style={{
                          marginLeft: '8px',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: '#0066FF',
                          display: 'inline-block',
                          animation: 'pulse 2s infinite'
                        }} />
                      )}
                    </button>
                    <div className="section-items">
                      {user?.beta_tester && (
                        <button className="nav-item" onClick={() => navigate('/build/embed')}>
                          <Code size={20} />
                          <div className="item-content">
                            <div className="item-title">Share Your Agent</div>
                          </div>
                        </button>
                      )}
                      <button 
                        className="nav-item" 
                        onClick={() => {
                          navigate('/build/conversations');
                          // Dispatch event to reset ConversationHistory to table view if in chat view
                          window.dispatchEvent(new CustomEvent('navigateToConversations'));
                        }}
                      >
                        {notifications > 0 && (
                          <span className="notification-dot"></span>
                        )}
                        <BarChart3 size={20} />
                        <div className="item-content">
                          <div className="item-title">View User Conversations</div>
                        </div>
                      </button>
                      <button className="nav-item" onClick={() => navigate('/build/agentsettings')}>
                        <Settings size={20} />
                        <div className="item-content">
                          <div className="item-title">Agent Settings</div>
                        </div>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* Discover Navigation */}
          {mode === 'discover' && (
            <>
              {/* Discover Agents menu option hidden for initial release */}
              {/* <div className="nav-section">
                <button className="section-header direct-nav" onClick={() => navigate('/')}>
                  <Search size={20} />
                  Discover Agents
                </button>
              </div> */}

              <div className="nav-section">
                <button 
                  className="section-header" 
                  onClick={() => toggleSubsection('pendle')}
                >
                  <img src="/pendle-pendle-logo.png" alt="Pendle" style={{ width: '20px', height: '20px' }} />
                  Pendle
                  {expandedSubsections.includes('pendle') ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </button>
                {expandedSubsections.includes('pendle') && (
                  <div className="section-items">
                    {pendleAgents.length > 0 ? (
                      pendleAgents.map((agent) => (
                        <button
                          key={agent.id}
                          className="nav-item"
                          onClick={() => {
                            // Launch new chat with this agent
                            const agentData = {
                              id: agent.id,
                              name: agent.name,
                              agent_avatar: agent.agent_avatar,
                              description: agent.description,
                              creator_name: agent.creator_name
                            };
                            setSelectedAgent(agentData);
                            setSelectedConversationId(null); // New chat
                            setIsInChat(true);
                            setSearchParams({ agent: agent.id.toString() });
                          }}
                        >
                          {agent.agent_avatar ? (
                            <img 
                              src={agent.agent_avatar} 
                              alt={agent.name}
                              className="agent-avatar"
                              style={{ width: 32, height: 32, borderRadius: '50%' }}
                            />
                          ) : (
                            <Bot size={16} />
                          )}
                          <div className="item-content">
                            <span className="item-title">{agent.name}</span>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="nav-item" style={{ color: 'var(--text-muted)', padding: '0.5rem 1rem' }}>
                        No agents available
                      </div>
                    )}
                  </div>
                )}
              </div>

              {isAuthenticated && (
                <div className="nav-section">
                  <button className="section-header" onClick={() => toggleSubsection('my-chats')}>
                    <MessageCircle size={20} />
                    My Chats
                    {expandedSubsections.includes('my-chats') ? (
                      <ChevronDown size={16} />
                    ) : (
                      <ChevronRight size={16} />
                    )}
                  </button>
                  {expandedSubsections.includes('my-chats') && (
                  <div className="section-items">
                    {loadingChats ? (
                      <>
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="nav-item chat-skeleton">
                            <div className="skeleton-avatar"></div>
                            <div className="item-content">
                              <div className="skeleton-line skeleton-title"></div>
                              <div className="skeleton-line skeleton-subtitle"></div>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : recentChats.length > 0 ? (
                      <>
                        {(showAllChats ? recentChats : recentChats.slice(0, 5)).map((chat) => (
                          <button
                            key={chat.id}
                            className="nav-item"
                            onClick={() => handleChatClick(chat)}
                          >
                            {chat.agent_avatar ? (
                              <img 
                                src={chat.agent_avatar} 
                                alt={chat.agent_name}
                                className="agent-avatar"
                                style={{ width: 32, height: 32, borderRadius: '50%' }}
                              />
                            ) : (
                              <MessageSquare size={16} />
                            )}
                            <div className="item-content">
                            <span className="item-title">
                              {chat.title && chat.title.trim() ? 
                                (chat.title.length > 50 ? 
                                  chat.title.substring(0, 50) + '...' : 
                                  chat.title
                                ) : 
                                `New Chat ${chat.id}`
                              }
                            </span>
                              <span className="item-subtitle">with {chat.agent_name} by {chat.creator_name}</span>
                            </div>
                          </button>
                        ))}
                        {recentChats.length > 5 && (
                          <button
                            className="nav-item"
                            onClick={() => setShowAllChats(!showAllChats)}
                          >
                            <MoreHorizontal size={16} />
                            <div className="item-content">
                              <span className="item-title">
                                {showAllChats ? 'Show Less' : `Show All (${recentChats.length})`}
                              </span>
                            </div>
                          </button>
                        )}
                      </>
                    ) : (
                      <div className="nav-item">
                        <MessageCircle size={16} />
                        <div className="item-content">
                          <span className="item-title">No chats yet</span>
                          <span className="item-subtitle">Start chatting with agents</span>
                        </div>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              )}

              {/* Settings button - hidden in production */}
              {process.env.NODE_ENV !== 'production' && (
                <div className="nav-section">
                  <AuthGuard>
                    <button 
                      className="section-header direct-nav" 
                      onClick={() => {
                        // More explicit check for talkto route
                        if ((mode === 'discover' || location.pathname === '/' || location.pathname.startsWith('/talkto')) && isAuthenticated) {
                          // Navigate to settings for authenticated users on discover mode
                          navigate('/settings');
                        } else {
                          // Navigate to settings for build mode or unauthenticated users
                          navigate('/settings');
                        }
                      }}
                    >
                      <Settings size={20} />
                      Settings
                    </button>
                  </AuthGuard>
                </div>
              )}
            </>
          )}
        </nav>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {isInChat ? (
          <PublicChatInterface 
            key={selectedAgent?.id || 'no-agent'}
            agent={selectedAgent} 
            conversationId={selectedConversationId}
            onBack={handleBackFromChat}
            onUpdateUrl={handleUpdateUrl}
          />
        ) : (
          <div className="content-body">
            {React.Children.map(props.children, child => 
              React.isValidElement(child) ? React.cloneElement(child, { ...props }) : child
            )}
          </div>
        )}
      </div>

      {/* Sidebar Overlay for Mobile */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* Signup/Signin Modal */}
      {showSignupModal && (
        <div className="modal-overlay" onClick={() => setShowSignupModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close"
              onClick={() => setShowSignupModal(false)}
            >
              <X size={24} />
            </button>
            <div className="auth-mode-toggle">
              <button
                className={authMode === 'signin' ? 'active' : ''}
                onClick={() => setAuthMode('signin')}
              >
                Sign In
              </button>
              <button
                className={authMode === 'signup' ? 'active' : ''}
                onClick={() => setAuthMode('signup')}
              >
                Sign Up
              </button>
            </div>
            <FirebaseAuth 
              mode={authMode}
              onSuccess={() => {
                console.log('Authentication successful, waiting for user sync...');
                // Modal will close automatically when user state is set (via useEffect above)
              }}
              onError={(error) => {
                console.error('Authentication error:', error);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Simple Data Provider
const dataProvider = {
  getList: (resource, params) => {
    return Promise.resolve({ data: [], total: 0 });
  },
  getOne: (resource, params) => {
    return Promise.resolve({ data: { id: params.id } });
  },
  getMany: (resource, params) => {
    return Promise.resolve({ data: [] });
  },
  getManyReference: (resource, params) => {
    return Promise.resolve({ data: [], total: 0 });
  },
  create: (resource, params) => {
    return Promise.resolve({ data: { ...params.data, id: Date.now() } });
  },
  update: (resource, params) => {
    return Promise.resolve({ data: { ...params.data } });
  },
  updateMany: (resource, params) => {
    return Promise.resolve({ data: params.ids });
  },
  delete: (resource, params) => {
    return Promise.resolve({ data: { id: params.id } });
  },
  deleteMany: (resource, params) => {
    return Promise.resolve({ data: params.ids });
  },
};

// React Admin Prototype - Production-ready layout component
const ReactAdminPrototype = ({ children, ...otherProps }) => {
  return <CustomLayout {...otherProps}>{children}</CustomLayout>;
};

export default ReactAdminPrototype;